/**
 * Withdraw an entry from a running league (demo-ready PR 9).
 * Opens with the counts from a dry run (played and awaiting games stay; the
 * future ones are forfeited or cancelled), then posts the host's choice:
 *   POST /leagues/:id/teams/:teamId/withdraw { future, hide_from_standings, message }
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiClient } from '../../../services/api/apiService';

const errorText = (error) => error?.response?.data?.message || error?.message || 'Something went wrong';
const games = (n) => `${n} game${n === 1 ? '' : 's'}`;

const WithdrawEntryDialog = ({ league, entryKey, name, unitLower = 'team', onDone, onClose }) => {
  const [preview, setPreview] = useState(null);
  const [future, setFuture] = useState('forfeit');
  const [hide, setHide] = useState(false);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const url = `/leagues/${league.id}/teams/${entryKey}/withdraw`;

  useEffect(() => {
    let alive = true;
    Promise.resolve()
      .then(() => apiClient.post(url, { dry_run: true }))
      .then((res) => alive && setPreview(res?.data?.data || null))
      .catch((err) => alive && setError(errorText(err)));
    return () => { alive = false; };
  }, [url]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiClient.post(url, { future, hide_from_standings: hide, message: message.trim() || undefined });
      onDone?.(res?.data?.message || `${name} withdrew`);
    } catch (err) {
      setError(errorText(err));
      setSaving(false);
    }
  };

  // On document.body: the league page's animated cards would otherwise hold the fixed backdrop
  return createPortal(
    <div className="league-dialog-backdrop" onClick={onClose}>
      <form
        className="league-game-form league-dialog"
        aria-label={`Withdraw ${name}`}
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); submit(); }}
      >
        <h5 className="league-game-form__title">Withdraw {name}</h5>
        {!preview && !error && <p className="league-dialog__note">Counting games…</p>}
        {preview && (
          <>
            <p className="league-dialog__note">
              {games(preview.played)} played and {games(preview.awaiting_result)} awaiting a result stay as they are.
              {preview.future > 0 ? ` Its ${games(preview.future)} still to play:` : ' It has no games still to play.'}
            </p>
            {league.fee_mode && league.fee_mode !== 'none' && (
              <p className="league-dialog__note" data-testid="withdraw-refund-note" style={{ color: '#fcd34d' }}>
                The season has started, so league fees already paid are not refunded automatically.
                Refunds are up to the host, who can refund any payment from Payments.
                {league.refund_policy ? ` Host's refund policy: ${league.refund_policy}` : ''}
              </p>
            )}
            {preview.future > 0 && (
              <div className="league-dialog__choices" role="radiogroup" aria-label="Future games">
                <label>
                  <input type="radio" name="future" value="forfeit" checked={future === 'forfeit'} onChange={() => setFuture('forfeit')} />
                  Forfeit {preview.future}: each opponent gets the win
                </label>
                <label>
                  <input type="radio" name="future" value="cancel" checked={future === 'cancel'} onChange={() => setFuture('cancel')} />
                  Cancel {preview.future}: they are taken off the schedule
                </label>
              </div>
            )}
            <label className="league-rules-form__check">
              <input type="checkbox" checked={hide} onChange={(e) => setHide(e.target.checked)} />
              Hide the {unitLower} from the standings (otherwise it stays, marked Withdrawn)
            </label>
            <label className="league-dialog__message">Optional message to the {unitLower}
              <textarea value={message} maxLength={200} rows={2} onChange={(e) => setMessage(e.target.value)} />
            </label>
          </>
        )}
        {error && <p className="league-game-form__error" role="alert">{error}</p>}
        <div className="league-game-form__actions">
          <button type="submit" className="league-game-form__submit league-dialog__danger" disabled={!preview || saving}>
            {saving ? 'Withdrawing…' : `Withdraw ${name}`}
          </button>
          <button type="button" className="league-game-form__cancel" onClick={onClose} disabled={saving}>Cancel</button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default WithdrawEntryDialog;
